import { z } from "zod";
import { expectedVersion, id, pageInput } from "./common";
import { longText, safeText } from "./text";

/** Lifecycle of a campaign. `archived` hides it from the default portfolio. */
export const CAMPAIGN_STATUSES = ["planning", "active", "paused", "completed", "archived"] as const;
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number];

export const CAMPAIGN_STATUS_LABELS: Record<CampaignStatus, string> = {
  planning: "Planning",
  active: "Active",
  paused: "Paused",
  completed: "Completed",
  archived: "Archived",
};

/** Which status changes are allowed (the server enforces this; the UI only offers these). */
export const CAMPAIGN_STATUS_TRANSITIONS: Record<CampaignStatus, readonly CampaignStatus[]> = {
  planning: ["active", "archived"],
  active: ["paused", "completed", "archived"],
  paused: ["active", "completed", "archived"],
  completed: ["active", "archived"],
  archived: ["planning"],
};

export const CAMPAIGN_CHANNELS = [
  "instagram",
  "facebook",
  "linkedin",
  "youtube",
  "tiktok",
  "x",
  "google_ads",
  "meta_ads",
  "email",
  "website",
  "other",
] as const;
export type CampaignChannel = (typeof CAMPAIGN_CHANNELS)[number];

export const CAMPAIGN_CHANNEL_LABELS: Record<CampaignChannel, string> = {
  instagram: "Instagram",
  facebook: "Facebook",
  linkedin: "LinkedIn",
  youtube: "YouTube",
  tiktok: "TikTok",
  x: "X",
  google_ads: "Google Ads",
  meta_ads: "Meta Ads",
  email: "Email",
  website: "Website",
  other: "Other",
};

/** ISO 4217 code, e.g. INR, USD. */
export const currencyCode = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{3}$/, "Use a 3-letter currency code, e.g. INR");

/** Calendar date without a time zone (YYYY-MM-DD). */
export const calendarDate = z.iso.date("Use a date like 2026-11-30");

/** Budget in the currency's minor unit (paise, cents), so no floating-point money. */
export const budgetMinor = z.number().int().min(0).max(1_000_000_000_000);

const campaignFields = z.object({
  name: safeText(2, 120),
  objective: longText(4000),
  audience: longText(4000),
  startsOn: calendarDate.nullable(),
  endsOn: calendarDate.nullable(),
  budgetAmountMinor: budgetMinor.nullable(),
  budgetCurrency: currencyCode.nullable(),
  channels: z
    .array(z.enum(CAMPAIGN_CHANNELS))
    .max(CAMPAIGN_CHANNELS.length)
    .refine((c) => new Set(c).size === c.length, "Each channel only once"),
  ownerUserId: id,
});

type CampaignShape = Partial<z.infer<typeof campaignFields>>;

/** Rules across fields. Applied to create input here, and to the merged record on update. */
export const campaignConsistencyIssues = (c: CampaignShape) => {
  const issues: { path: keyof CampaignShape; message: string }[] = [];
  if (c.startsOn && c.endsOn && c.endsOn < c.startsOn)
    issues.push({ path: "endsOn", message: "The end date can't be before the start date" });
  if (c.budgetAmountMinor !== null && c.budgetAmountMinor !== undefined && !c.budgetCurrency)
    issues.push({ path: "budgetCurrency", message: "Choose a currency for the budget" });
  return issues;
};

export const createCampaignInput = campaignFields.superRefine((c, ctx) => {
  for (const issue of campaignConsistencyIssues(c))
    ctx.addIssue({ code: "custom", path: [issue.path], message: issue.message });
});
export type CreateCampaignInput = z.infer<typeof createCampaignInput>;

/** Only the fields being changed; cross-field rules are checked against the stored record. */
export const updateCampaignInput = campaignFields.partial().extend({
  campaignId: id,
  expectedVersion,
});
export type UpdateCampaignInput = z.infer<typeof updateCampaignInput>;

export const setCampaignStatusInput = z.object({
  campaignId: id,
  status: z.enum(CAMPAIGN_STATUSES),
  expectedVersion,
});

/** The people working on a campaign. Responsibility, not access (ADR 0012). */
export const setCampaignTeamInput = z.object({
  campaignId: id,
  userIds: z
    .array(id)
    .max(50)
    .refine((ids) => new Set(ids).size === ids.length, "Each person only once"),
  expectedVersion,
});
export type SetCampaignTeamInput = z.infer<typeof setCampaignTeamInput>;

export const campaignIdInput = z.object({ campaignId: id });

export const listCampaignsInput = pageInput.extend({
  /** Default (empty): everything except archived. */
  statuses: z.array(z.enum(CAMPAIGN_STATUSES)).max(CAMPAIGN_STATUSES.length).optional(),
  ownerUserId: id.optional(),
  channel: z.enum(CAMPAIGN_CHANNELS).optional(),
  search: safeText(0, 100).optional(),
});
export type ListCampaignsInput = z.infer<typeof listCampaignsInput>;
